/**
 * 时间范围可见性控制模块
 * Time Range Visibility Controller
 * 
 * 用法 / Usage:
 * 1. 在 HTML 元素上添加 data-time-range 属性
 *    Add data-time-range attribute to HTML element
 * 
 * 示例 / Example:
 * <div data-time-range="2026/01/27 20:22:00 PST,2026/01/28 20:22:00 PST">
 *   这个内容只在指定时间范围内显示
 * </div>
 * 
 * 2. 或者使用 data-time-start 和 data-time-end 分别指定
 *    Or use data-time-start and data-time-end separately
 * 
 * <div data-time-start="2026/01/27 20:22:00 PST" data-time-end="2026/01/28 20:22:00 PST">
 *   这个内容只在指定时间范围内显示
 * </div>
 * 
 * 时间格式支持 / Supported time formats:
 * - "2026/01/27 20:22:00 PST"
 * - "2026/01/27 20:22:00 EST"
 * - "2026-01-27T20:22:00-08:00" (ISO 8601)
 * - "2026-01-27 20:22:00" (假定为 UTC)
 */

class TimeRangeVisibility {
  constructor() {
    this.checkInterval = 1000; // 每60秒检查一次 (Check every 60 seconds)
    this.timers = new Map();
    this.init();
  }

  /**
   * 解析时区缩写为 UTC 偏移量
   * Parse timezone abbreviation to UTC offset
   */
  parseTimezone(tz) {
    const timezones = {
      'PST': -8,  // 太平洋标准时间
      'PDT': -7,  // 太平洋夏令时间
      'MST': -7,  // 山地标准时间
      'MDT': -6,  // 山地夏令时间
      'CST': -6,  // 中部标准时间
      'CDT': -5,  // 中部夏令时间
      'EST': -5,  // 东部标准时间
      'EDT': -4,  // 东部夏令时间
      'UTC': 0,
      'GMT': 0
    };
    return timezones[tz.toUpperCase()] || 0;
  }

  /**
   * 解析时间字符串为 Date 对象
   * Parse time string to Date object
   */
  parseTimeString(timeStr) {
    if (!timeStr) return null;
    
    timeStr = timeStr.trim();

    // 尝试匹配带时区缩写的格式: "2026/01/27 20:22:00 PST"
    const tzMatch = timeStr.match(/^(.+?)\s+([A-Z]{3,4})$/);
    if (tzMatch) {
      const [, dateTimeStr, tz] = tzMatch;
      const offset = this.parseTimezone(tz);
      
      // 将 "/" 替换为 "-" 以便标准化
      const normalizedStr = dateTimeStr.replace(/\//g, '-');
      
      // 创建本地时间
      const parts = normalizedStr.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
      if (parts) {
        const [, year, month, day, hour, minute, second] = parts;
        // 创建 UTC 时间,然后应用时区偏移
        const utcTime = Date.UTC(year, month - 1, day, hour, minute, second);
        return new Date(utcTime - offset * 3600000);
      }
    }

    // 尝试标准 Date 解析
    const date = new Date(timeStr);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * 解析时间范围字符串
   * Parse time range string
   */
  parseTimeRange(rangeStr) {
    if (!rangeStr) return null;
    
    const times = rangeStr.split(',').map(t => t.trim());
    if (times.length !== 2) {
      console.warn('TimeRangeVisibility: Invalid time range format. Expected "start,end"');
      return null;
    }

    const startTime = this.parseTimeString(times[0]);
    const endTime = this.parseTimeString(times[1]);

    if (!startTime || !endTime) {
      console.warn('TimeRangeVisibility: Failed to parse time range:', rangeStr);
      return null;
    }

    return { startTime, endTime };
  }

  /**
   * 检查当前时间是否在范围内
   * Check if current time is within range
   */
  isInRange(startTime, endTime) {
    const now = new Date();
    return now >= startTime && now <= endTime;
  }

  /**
   * 更新元素的可见性
   * Update element visibility
   */
  updateElementVisibility(element, isVisible) {
    if (isVisible) {
      element.style.display = '';
      element.removeAttribute('hidden');
    } else {
      element.style.display = 'none';
      element.setAttribute('hidden', '');
    }
  }

  /**
   * 检查并更新单个元素
   * Check and update single element
   */
  checkElement(element) {
    // 优先使用 data-time-range
    let rangeStr = element.dataset.timeRange;
    let range = null;

    if (rangeStr) {
      range = this.parseTimeRange(rangeStr);
    } else {
      // 使用 data-time-start 和 data-time-end
      const startStr = element.dataset.timeStart;
      const endStr = element.dataset.timeEnd;
      
      if (startStr && endStr) {
        const startTime = this.parseTimeString(startStr);
        const endTime = this.parseTimeString(endStr);
        if (startTime && endTime) {
          range = { startTime, endTime };
        }
      }
    }

    if (!range) {
      console.warn('TimeRangeVisibility: No valid time range found for element', element);
      return;
    }

    const isVisible = this.isInRange(range.startTime, range.endTime);
    this.updateElementVisibility(element, isVisible);

    // 记录调试信息 (可选)
    if (element.dataset.timeDebug === 'true') {
      console.log('TimeRangeVisibility:', {
        element,
        start: range.startTime,
        end: range.endTime,
        now: new Date(),
        isVisible
      });
    }
  }

  /**
   * 检查所有元素
   * Check all elements
   */
  checkAllElements() {
    const elements = document.querySelectorAll('[data-time-range], [data-time-start][data-time-end]');
    elements.forEach(element => this.checkElement(element));
  }

  /**
   * 初始化
   * Initialize
   */
  init() {
    // 页面加载时立即检查
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.checkAllElements();
      });
    } else {
      this.checkAllElements();
    }

    // 定期检查
    this.timers.set('main', setInterval(() => {
      this.checkAllElements();
    }, this.checkInterval));

    // 监听 DOM 变化
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.matches('[data-time-range], [data-time-start][data-time-end]')) {
              this.checkElement(node);
            }
            // 检查子元素
            const children = node.querySelectorAll('[data-time-range], [data-time-start][data-time-end]');
            children.forEach(child => this.checkElement(child));
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * 销毁实例
   * Destroy instance
   */
  destroy() {
    this.timers.forEach((timer) => clearInterval(timer));
    this.timers.clear();
  }

  /**
   * 手动刷新所有元素
   * Manually refresh all elements
   */
  refresh() {
    this.checkAllElements();
  }
}

// 自动初始化
const timeRangeVisibility = new TimeRangeVisibility();

// 暴露到全局
window.TimeRangeVisibility = TimeRangeVisibility;
window.timeRangeVisibility = timeRangeVisibility;
